import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import * as MicrosoftGraph from '@microsoft/microsoft-graph-client';
import * as Sentry from '@sentry/angular';
import { environment } from 'src/environments/environment';

export interface IUser {
  email: string;
  name?: string;
  avatarSource?: string;
}

export interface IResponse {
  isOk: boolean;
  data?: IUser;
  message?: string;
}

const defaultUser: IUser = {
  email: '',
  name: '',
  avatarSource: undefined,
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _user: IUser | null = defaultUser;
  loginDisplay = false;

  get loggedIn(): boolean {
    return !!this._user;
  }

  constructor(private router: Router, private msalService: MsalService) {}

  async getUser() {
    // Local dev bypass: no MSAL account — return a stub so nothing dereferences null.
    if (environment.authBypass) {
      this._user = { email: 'dev.local@calysta.eu', name: 'Dev (local bypass)', avatarSource: defaultUser.avatarSource };
      Sentry.setUser({ email: this._user.email });
      return { isOk: true, data: this._user };
    }

    try {
      //get user name, email and profile picture from msal user
      const msalUserInfo = this.msalService.instance.getActiveAccount();

      var picture = await this.getProfilePicture();

      this._user = {
        email: msalUserInfo.username,
        name: msalUserInfo.name,
        avatarSource: picture || defaultUser.avatarSource,
      };
      Sentry.setUser({ email: this._user.email });
      return {
        isOk: true,
        data: this._user,
      };
    } catch {
      return {
        isOk: false,
        data: null,
      };
    }
  }

  /**
   * Resolves to `null` (never rejects) whenever no photo is available -- most Azure AD accounts
   * don't have one set, so Graph's `ImageNotFoundException` 404 here is an expected outcome, not
   * a failure worth surfacing. Rejecting used to bubble up through getUser()'s try/catch and wipe
   * out name/email along with the avatar for every such user.
   */
  async getProfilePicture(): Promise<string | null> {
    return new Promise((resolve) => {
      const graphClient = MicrosoftGraph.Client.init({
        authProvider: async (done) => {
          this.msalService
            .acquireTokenSilent({
              scopes: ['User.Read'],
            })
            .subscribe({
              next: (token: any) => {
                done(null, token.accessToken);
              },
              error: (err: any) => {
                done(err, null);
              },
            });
        },
      });

      graphClient
        .api('/me/photo/$value')
        .get()
        .then((response) => {
          const blob = response as Blob;

          let reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onloadend = function () {
            const base64data = reader.result;
            resolve(base64data.toString());
          };
        })
        .catch(() => resolve(null));
    });
  }

  async logOut() {
    localStorage.removeItem('permissions');
    this.msalService.logoutRedirect();
  }

  getEmail(): string {
    return this._user?.email;
  }

  isUserGuest(): boolean {
    if (environment.authBypass) return false;

    const user = this.msalService.instance.getActiveAccount();

    //if userName (mail address) dont contains @Calysta.eu
    if (user.username.toLowerCase().indexOf('@calysta.eu') === -1) {
      return true;
    }
  }
}
